// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { PositionsBalanceFormatter } from '@shared/commons/cache/Formatters/PositionsBalanceFormatter';
import { RiskPlan } from '@shared/commons/cache/RiskPlan';
import { DataCache } from '@shared/commons/DataCache';
import { Quantity } from '@shared/commons/UtilsClasses/Quantity';
import { Resources } from '@shared/localizations/Resources';
import { InstrumentUtils } from '@shared/utils/Instruments/InstrumentUtils';
import { PriceFormatter } from '@shared/utils/Instruments/PriceFormatter';
import { DateTimeUtils } from '@shared/utils/Time/DateTimeUtils';
import { BaseItem, ColumnData } from './BaseItem';
import { ColumnParams } from './ColumnParams';
import { PositionItem } from './PositionItem';
import { TableTotalsVisibility } from './TableTotalsVisibility';
import { QTAlignment } from '../elements/QuickTable/QuickTableColumn';
import { ThemeManager } from '../misc/ThemeManager';
import { WDSettingsUtils } from '../UtilsClasses/WDGeneralSettingsUtils';
export class PositionsBalanceItem extends BaseItem {
    constructor(positionsBalance, sessionSettings) {
        super(sessionSettings);
        this.positionsBalance = positionsBalance;
    }
    get Instrument() {
        return this.positionsBalance ? this.positionsBalance.Instrument : null;
    }
    getQuickTableCellForeground(column) {
        if (column === 0) {
            return ThemeManager.colors.tableSymbolForeground;
        }
        else {
            return super.getQuickTableCellForeground(column);
        }
    }
    GetCurrentInstrument() {
        return this.Instrument;
    }
    getBalanceCurrencyName() {
        var _a, _b, _c;
        return (_c = (_b = (_a = this.positionsBalance) === null || _a === void 0 ? void 0 : _a.Asset) === null || _b === void 0 ? void 0 : _b.Name) !== null && _c !== void 0 ? _c : DataCache.getUserBaseCurrency();
    }
    getColumnData(column) {
        let value = null;
        let formattedValue = null;
        if (this.positionsBalance === null) {
            value = null;
            formattedValue = '';
        }
        value = this.getColumnValue(column);
        const ins = this.Instrument;
        switch (column) {
            case 1:
            case 5:
            case 7: {
                const acc = this.positionsBalance.getAccountWithMaxAmountPrecision();
                const pT = this.positionsBalance.ProductType;
                const showInLots = WDSettingsUtils.displayAmountInLots();
                if (!showInLots) {
                    value = Quantity.convertQuantityValue(new Quantity(value, true), ins, showInLots, acc, pT);
                }
                formattedValue = !isNaN(value)
                    ? DataCache.formatVolume(ins, value, showInLots, pT, acc)
                    : PositionItem.NOT_AVAILABLE;
                break;
            }
            case 2:
                formattedValue = !isNaN(value)
                    ? ins.formatPricePrecision(value, true, true)
                    : PositionItem.NOT_AVAILABLE;
                break;
            case 3:
            case 6:
            case 8:
                formattedValue = !isNaN(value) ? ins.formatPrice(value, true, true) : PositionItem.NOT_AVAILABLE;
                break;
            case 4:
            case 9:
            case 10:
            case 13:
                formattedValue = !isNaN(value)
                    ? PriceFormatter.formatPrice(value, 2, false) + ' ' + this.getBalanceCurrencyName()
                    : PositionItem.NOT_AVAILABLE;
                break;
            case 12:
                formattedValue = value !== null ? DateTimeUtils.formatDate(value, 'DD.MM.YYYY') : '';
                break;
            default:
                formattedValue = value ? value.toString() : '';
                break;
        }
        return new ColumnData(value, formattedValue);
    }
    getColumnValue(column) {
        const positionsBalance = this.positionsBalance;
        if (positionsBalance === null) {
            return null;
        }
        const ins = positionsBalance.Instrument;
        switch (column) {
            case 0:
                return PositionsBalanceFormatter.SymbolStr(positionsBalance);
            case 1:
                return positionsBalance.NetPositionQty;
            case 2:
                return positionsBalance.BreakEvenPoint;
            case 3:
                return positionsBalance.CurrentPrice;
            case 4: {
                const balanceCurrency = this.getBalanceCurrencyName(); // первая по счету колонка с ассетом следит за соблюдением единого ассета во всей таблице
                if (balanceCurrency !== this.Currency) {
                    this.Currency = balanceCurrency;
                    this.isNeedFireUpdate = true;
                }
                return positionsBalance.GrossPnL;
            }
            case 5:
                return positionsBalance.LongQty;
            case 6:
                return positionsBalance.AverageLong;
            case 7:
                return positionsBalance.ShortQty;
            case 8:
                return positionsBalance.AverageShort;
            case 9:
                return positionsBalance.GrossExposure;
            case 10:
                return positionsBalance.NetExposure;
            case 11:
                return positionsBalance.StrikePrice;
            case 12:
                return positionsBalance.ExpDate;
            case 13:
                return positionsBalance.PositionValue;
            case 14:
                return InstrumentUtils.getInstrumentTypeStringLocalized(ins.InstrType);
            case 15:
                return PositionsBalanceFormatter.ProductTypeStr(positionsBalance);
            default:
                return null;
        }
    }
    Recalc() {
        if (this.positionsBalance) {
            this.positionsBalance.RecalculatePositionBalance();
        }
    }
    // public override getFormatTotalValue  (column, value, precisionMode = false)
    // {
    //     let formattedValue = "";
    //     let dc = DataCache;
    //     switch (column)
    //     {
    //         case 5:
    //         case 8:
    //             if (precisionMode)
    //                 return 2;
    //             formattedValue = value.toFixed(2)
    //             break;
    //     }
    //     return formattedValue;
    // }
    ColumnCount() {
        return PositionsBalanceItem.columnsParams.length;
    }
    GetColumnParams(column) {
        const result = PositionsBalanceItem.columnsParams[column];
        const isHidden = result.DefaultHidden || Resources.isHidden(result.HeaderKey);
        result.Hidden = isHidden;
        result.MenuIndex = this.GetMenuIndex(result.HeaderKey);
        if (column === 15) {
            // Product type
            result.Hidden = !RiskPlan.showProductType;
        }
        return result;
    }
    getFormatTotalValue(column, value) {
        let formattedValue = '';
        switch (column) {
            case 4:
            case 9:
            case 10:
            case 13:
                formattedValue = value != null ? PriceFormatter.formatPrice(value, 2) + ' ' + this.getBalanceCurrencyName() : '';
                break;
            default:
                formattedValue = value != null ? PriceFormatter.formatPrice(value, 2) : '';
                break;
        }
        return formattedValue;
    }
    GetMenuIndex(headerKey) {
        if (PositionsBalanceItem.ListMenuIndex.Contains(headerKey)) {
            return PositionsBalanceItem.ListMenuIndex.indexOf(headerKey);
        }
        else {
            return 1000;
        }
    }
    getTotalValuePrecision(column, totalCurrencyAfterFiltration) {
        return 2;
    }
}
/// <summary>
/// Здесь хранятся настройки колонок (один экземпляр на класс)
/// </summary>
PositionsBalanceItem.columnsParams = [
    /* 0 */ new ColumnParams('panel.positionsBalance.Symbol', 75, QTAlignment.Left, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positionsBalance.NetPositionQty', 120, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.positionsBalance.BreakEvenPoint', 120, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positionsBalance.CurrentPrice', 75, QTAlignment.Right, TableTotalsVisibility.None, true, false),
    new ColumnParams('panel.positionsBalance.GrossPL', 75, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 5 */ new ColumnParams('panel.positionsBalance.LongQty', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.positionsBalance.AverageLong', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positionsBalance.ShortQty', 75, QTAlignment.Right, TableTotalsVisibility.All, false, false),
    new ColumnParams('panel.positionsBalance.AverageShort', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positionsBalance.GrossExposure', 95, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    /* 10 */ new ColumnParams('panel.positionsBalance.NetExposure', 90, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.positionsBalance.StrikePrice', 75, QTAlignment.Right, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positionsBalance.ExpDate', 75, QTAlignment.Left, TableTotalsVisibility.None, false, false),
    new ColumnParams('panel.positionsBalance.PositionValue', 100, QTAlignment.Right, TableTotalsVisibility.All, true, false),
    new ColumnParams('panel.positionsBalance.SymbType', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true),
    /* 15 */ new ColumnParams('panel.positionsBalance.ProductType', 75, QTAlignment.Left, TableTotalsVisibility.None, false, true)
];
PositionsBalanceItem.ListMenuIndex = [
    'panel.positionsBalance.Symbol',
    'panel.positionsBalance.NetPositionQty',
    'panel.positionsBalance.BreakEvenPoint',
    'panel.positionsBalance.CurrentPrice',
    'panel.positionsBalance.GrossPL',
    'panel.positionsBalance.LongQty',
    'panel.positionsBalance.AverageLong',
    'panel.positionsBalance.ShortQty',
    'panel.positionsBalance.AverageShort',
    'panel.positionsBalance.GrossExposure',
    'panel.positionsBalance.NetExposure',
    'panel.positionsBalance.StrikePrice',
    'panel.positionsBalance.ExpDate',
    'panel.positionsBalance.PositionValue',
    'panel.positionsBalance.SymbType',
    'panel.positionsBalance.ProductType'
];
